import { getAxios } from '@api';
import { otherServiceIdsOf } from '@utilities/functions';

// Zuschläge/Hinweise optional auf einzelne Leistungen des Leistungsträgers einschränken: die Hauptleistung
// (Hotel, Zug, Fähre, Flug → validForMainService), einzelne Zug-/Fährverbindungen (validForTrainRoutes,
// validForFerryRoutes) und/oder Zusatzleistungen (validForOtherServices). Leere Auswahl = gilt für alle Leistungen.
const MAIN_SERVICE_LABELS = {
    hotel: 'Hotelleistung',
    train: 'Zugleistung',
    ferry: 'Fährleistung',
    airline: 'Flugleistung',
};

// Die Optionen werden selbst geladen: je nach Tab liefert der Provider-CRUD otherServices/Verbindungen nur als
// IRIs. Formular und Liste teilen sich kurz denselben Request; danach wird neu geladen, damit gerade angelegte
// Verbindungen/Zusatzleistungen auftauchen.
const optionsCache = {};
const OPTIONS_CACHE_MS = 30000;

const loadServiceOptions = function (api, id, providerType) {
    const key = `${api}/${id}`;
    if (!optionsCache[key]) {
        const toList = response => Array.isArray(response.data) ? response.data : (response.data['hydra:member'] || []);
        optionsCache[key] = Promise.all([
            getAxios('other_services', {
                'provider.id': id,
                _groups: ['other_service_read', 'other_service_type_read'],
            }).then(toList),
            providerType === 'train'
                ? getAxios('train_routes', {
                    'train.id': id,
                    _groups: ['provider_train_route', 'provider_train_route:trainStation', 'location_train_station'],
                }).then(toList)
                : Promise.resolve([]),
            providerType === 'ferry'
                ? getAxios('ferry_routes', { 'ferry.id': id, _groups: ['ferry_route_read'] }).then(toList)
                : Promise.resolve([]),
        ]).then(([otherServices, trainRoutes, ferryRoutes]) => ({ otherServices, trainRoutes, ferryRoutes }))
            .finally(() => setTimeout(() => { delete optionsCache[key]; }, OPTIONS_CACHE_MS))
            .catch(error => {
                delete optionsCache[key];
                throw error;
            });
    }
    return optionsCache[key];
};

const routeOption = function (kind, route) {
    let label = route.routeString;
    if (kind === 'train') {
        const start = route.startTrainStation && route.startTrainStation.name;
        const end = route.endTrainStation && route.endTrainStation.name;
        const extras = [
            route.type === 'night' ? 'Nachtzug' : null,
            route.trainType && route.trainType !== 'standard' ? route.trainType : null,
        ].filter(Boolean);
        label = [start, end].filter(Boolean).join(' – ') + (extras.length ? ` (${extras.join(', ')})` : '');
    }
    return { id: `${kind}-${route.id}`, routeKind: kind, routeId: route.id, label: `Verbindung: ${label}` };
};

export default {
    data: function () {
        return {
            serviceOptions: { otherServices: [], trainRoutes: [], ferryRoutes: [] },
        };
    },

    created: function () {
        this.loadServiceOptions();
    },

    watch: {
        '$store.state.data.id': function () {
            this.loadServiceOptions();
        },

        // Formulare (TabForm): beim Öffnen neu laden
        visible: function (visible) {
            if (visible) {
                this.loadServiceOptions();
            }
        },
    },

    computed: {
        mainServiceOption: function () {
            const data = this.$store.state.data;
            const label = data && MAIN_SERVICE_LABELS[data.providerType];
            return label ? { id: 'main', isMainService: true, label } : null;
        },

        otherServiceOptions: function () {
            const data = this.$store.state.data;
            if (!data || !data.providerType) {
                return [];
            }
            return [
                ...this.mainServiceOption ? [this.mainServiceOption] : [],
                ...this.serviceOptions.trainRoutes.map(route => routeOption('train', route)),
                ...this.serviceOptions.ferryRoutes.map(route => routeOption('ferry', route)),
                ...this.serviceOptions.otherServices,
            ];
        },

        validForOtherServices: {
            get: function () {
                return this.otherServiceOptions.filter(option => this.isServiceOptionSelected(this.form, option));
            },

            set: function (value) {
                const selected = value || [];
                const routeIris = (kind, api) => selected
                    .filter(option => option.routeKind === kind)
                    .map(option => `/api/${api}/${option.routeId}`);
                this.form.validForMainService = selected.some(option => option.isMainService);
                this.form.validForTrainRoutes = routeIris('train', 'train_routes');
                this.form.validForFerryRoutes = routeIris('ferry', 'ferry_routes');
                this.form.validForOtherServices = selected
                    .filter(option => !option.isMainService && !option.routeKind)
                    .map(option => `/api/other_services/${option.id}`);
            }
        },
    },

    methods: {
        loadServiceOptions: function () {
            const data = this.$store.state.data;
            if (!data || !data.id || !data.providerType) {
                return;
            }
            loadServiceOptions(this.$store.state.api, data.id, data.providerType)
                .then(options => { this.serviceOptions = options; })
                .catch(() => {});
        },

        isServiceOptionSelected: function (item, option) {
            if (option.isMainService) {
                return !!item.validForMainService;
            }
            if (option.routeKind) {
                const routes = option.routeKind === 'train' ? item.validForTrainRoutes : item.validForFerryRoutes;
                return otherServiceIdsOf(routes).includes(option.routeId);
            }
            return otherServiceIdsOf(item.validForOtherServices).includes(option.id);
        },

        otherServiceLabel: function (option) {
            if (!option) {
                return '';
            }
            if (option.label) {
                return option.label;
            }
            const type = option.otherServiceType && option.otherServiceType.name ? option.otherServiceType.name : '';
            return option.serviceDescription && option.serviceDescription !== type
                ? [type, option.serviceDescription].filter(Boolean).join(' – ')
                : type;
        },

        restrictedOtherServiceNames: function (item) {
            return this.otherServiceOptions
                .filter(option => this.isServiceOptionSelected(item, option))
                .map(option => this.otherServiceLabel(option));
        },
    },
};
